export const dashboardPermissionActions = ["read", "create", "write", "archive", "delete", "publish"] as const

export type DashboardPermissionAction = (typeof dashboardPermissionActions)[number]

export const dashboardPermissionResources = [
  "playbrain.users",
  "playbrain.roles",
  "playbrain.emailTemplates",
  "playbrain.contractMaker",
  "playbrain.tempPage",
  "playbrain.goalSetting",
  "playbrain.website.news",
  "playbrain.website.careers",
  "playbrain.website.workProducts",
  "playbrain.website.workMarketing",
  "playbrain.website.workTags",
  "playbrain.website.companyHistory",
  "playbrain.website.playdataNews",
  "playbrain.website.playdataTerms",
  "playbrain.website.playdataPrivacy",
  "playbrain.website.playdataSettings",
  "playbrain.website.playdataNewsletter",
  "playbrain.website.assets",
  "playbrain.shortLinks",
  "taiyoro.users",
  "taiyoro.events",
  "taiyoro.posts",
  "taiyoro.meta",
  "taiyoro.analytics",
  "taiyoro.brackets",
  "taiyoro.featuredEvents",
  "taiyoro.calendarEmbed",
  "taiyoro.blobs",
  "taiyoro.legal",
  "taiyoro.dataCollection",
  "taiyoro.playData",
  "taiyoro.scouting",
  "studio.workspaces",
] as const

export type DashboardPermissionResource = (typeof dashboardPermissionResources)[number]

export type DashboardPermissionKey = `${DashboardPermissionResource}:${DashboardPermissionAction}`

const dashboardPermissionActionsByResource = {
  "playbrain.users": ["read", "create", "write", "archive", "delete"],
  "playbrain.roles": ["read", "create", "write", "archive", "delete"],
  // Read only: nothing in the product writes a template back. See the note on
  // the same entry in `apps/api-users/src/shared/domain/rolePermissions.ts`.
  "playbrain.emailTemplates": ["read"],
  "playbrain.contractMaker": ["read", "create", "write", "archive", "delete"],
  "playbrain.tempPage": ["read", "create", "write", "archive", "delete"],
  "playbrain.goalSetting": ["read", "create", "write", "archive", "delete"],
  "playbrain.website.news": dashboardPermissionActions,
  "playbrain.website.careers": dashboardPermissionActions,
  "playbrain.website.workProducts": dashboardPermissionActions,
  "playbrain.website.workMarketing": dashboardPermissionActions,
  "playbrain.website.workTags": dashboardPermissionActions,
  "playbrain.website.companyHistory": dashboardPermissionActions,
  "playbrain.website.playdataNews": dashboardPermissionActions,
  "playbrain.website.playdataTerms": dashboardPermissionActions,
  "playbrain.website.playdataPrivacy": dashboardPermissionActions,
  "playbrain.website.playdataSettings": dashboardPermissionActions,
  // Mirrors the API. A subscriber list has no draft/publish or archive
  // lifecycle, and `delete` stays apart from `write` because erasing a row
  // destroys the consent record and the cancellation date.
  "playbrain.website.playdataNewsletter": ["read", "create", "write", "delete"],
  "playbrain.website.assets": dashboardPermissionActions,
  // Mirrors the API's playbrain.shortLinks actions. `publish` is not a real
  // action: it is the hidden marker that makes a role convey Kutt's
  // ADMINISTRATOR level. The matrix never renders these as action checkboxes —
  // short links shows as two levels, Admin and User (see PermissionMatrix).
  "playbrain.shortLinks": ["read", "create", "write", "delete", "publish"],
  "taiyoro.users": dashboardPermissionActions,
  "taiyoro.events": dashboardPermissionActions,
  "taiyoro.posts": dashboardPermissionActions,
  "taiyoro.meta": dashboardPermissionActions,
  "taiyoro.analytics": dashboardPermissionActions,
  "taiyoro.brackets": dashboardPermissionActions,
  "taiyoro.featuredEvents": dashboardPermissionActions,
  "taiyoro.calendarEmbed": dashboardPermissionActions,
  "taiyoro.blobs": dashboardPermissionActions,
  "taiyoro.legal": dashboardPermissionActions,
  "taiyoro.dataCollection": dashboardPermissionActions,
  "taiyoro.playData": dashboardPermissionActions,
  "taiyoro.scouting": dashboardPermissionActions,
  // Mirrors the API: a workspace has no draft/publish or archive lifecycle.
  "studio.workspaces": ["read", "create", "write", "delete"],
} satisfies Record<DashboardPermissionResource, readonly DashboardPermissionAction[]>

export type DashboardPermissionGroup = {
  key: string
  label: string
  resources: DashboardPermissionResource[]
}

export type DashboardPermissionFeature = {
  key: string
  label: string
  description: string
  resources: DashboardPermissionResource[]
}

export const dashboardPermissionLabels = {
  "playbrain.users": "Playbrain User Page",
  "playbrain.roles": "Playbrain Roles Page",
  "playbrain.emailTemplates": "Playbrain Email & Subscriptions Page",
  "playbrain.contractMaker": "Contract Maker",
  "playbrain.tempPage": "Temp Page",
  "playbrain.goalSetting": "Goal Setting",
  "playbrain.website.news": "Playbrain Website News",
  "playbrain.website.careers": "Playbrain Website Careers",
  "playbrain.website.workProducts": "Playbrain Website Work Products",
  "playbrain.website.workMarketing": "Playbrain Website Work Marketing",
  "playbrain.website.workTags": "Playbrain Website Work Tags",
  "playbrain.website.companyHistory": "Playbrain Website Company History",
  "playbrain.website.playdataNews": "PLAYDATA Website News",
  "playbrain.website.playdataTerms": "PLAYDATA Website Terms",
  "playbrain.website.playdataPrivacy": "PLAYDATA Website Privacy Policy",
  "playbrain.website.playdataSettings": "PLAYDATA Website Settings",
  "playbrain.website.playdataNewsletter": "PLAYDATA Newsletter Subscribers",
  "playbrain.website.assets": "Playbrain Website Assets",
  "playbrain.shortLinks": "Short Links",
  "taiyoro.users": "Taiyoro CMS Users",
  "taiyoro.events": "Taiyoro CMS Events",
  "taiyoro.posts": "Taiyoro CMS Posts",
  "taiyoro.meta": "Taiyoro CMS Meta",
  "taiyoro.analytics": "Taiyoro CMS Analytics",
  "taiyoro.brackets": "Taiyoro CMS Brackets",
  "taiyoro.featuredEvents": "Taiyoro CMS Featured Events",
  "taiyoro.calendarEmbed": "Taiyoro CMS Calendar Embed",
  "taiyoro.blobs": "Taiyoro CMS Blobs",
  "taiyoro.legal": "Taiyoro CMS Legal",
  "taiyoro.dataCollection": "Taiyoro CMS Data Collection",
  "taiyoro.playData": "Taiyoro CMS Play Data",
  "taiyoro.scouting": "Taiyoro CMS Scouting",
  "studio.workspaces": "Studio Workspaces",
} satisfies Record<DashboardPermissionResource, string>

const taiyoroResources = [
  "taiyoro.users",
  "taiyoro.events",
  "taiyoro.posts",
  "taiyoro.meta",
  "taiyoro.analytics",
  "taiyoro.brackets",
  "taiyoro.featuredEvents",
  "taiyoro.calendarEmbed",
  "taiyoro.blobs",
  "taiyoro.legal",
  "taiyoro.dataCollection",
  "taiyoro.playData",
  "taiyoro.scouting",
] as const satisfies readonly DashboardPermissionResource[]

export const dashboardPermissionGroups: DashboardPermissionGroup[] = [
  {
    key: "playbrain-admin",
    label: "Playbrain Admin",
    resources: ["playbrain.users", "playbrain.roles", "playbrain.emailTemplates"],
  },
  {
    key: "playbrain-features",
    label: "Playbrain Features",
    resources: ["playbrain.contractMaker", "playbrain.tempPage", "playbrain.goalSetting"],
  },
  {
    key: "playbrain-website",
    label: "Playbrain Website",
    resources: [
      "playbrain.website.news",
      "playbrain.website.careers",
      "playbrain.website.workProducts",
      "playbrain.website.workMarketing",
      "playbrain.website.workTags",
      "playbrain.website.companyHistory",
      "playbrain.website.assets",
    ],
  },
  {
    key: "playdata-website",
    label: "PLAYDATA Website",
    resources: [
      "playbrain.website.playdataNews",
      "playbrain.website.playdataTerms",
      "playbrain.website.playdataPrivacy",
      "playbrain.website.playdataSettings",
      "playbrain.website.playdataNewsletter",
    ],
  },
  {
    key: "taiyoro-cms",
    label: "Taiyoro CMS",
    resources: [...taiyoroResources],
  },
  {
    key: "studio",
    label: "Studio",
    resources: ["studio.workspaces"],
  },
  {
    // Rendered specially: two level checkboxes (Admin / User), never the
    // action columns — Kutt only knows two roles, and the RBAC mapping behind
    // the levels is hidden (Admin = CRUD + the `publish` marker, User = CRUD).
    key: "short-links",
    label: "Short Links",
    resources: ["playbrain.shortLinks"],
  },
]

/**
 * Short links renders as two levels, not action checkboxes — Kutt only knows
 * ADMIN and USER. User is the CRUD set; Admin is the CRUD set plus the hidden
 * `publish` marker the resolver reads as "Kutt administrator". These helpers
 * are the whole mapping, so the component stays presentation-only.
 */
export type ShortLinksLevel = "admin" | "user"

const shortLinksCrudPermissions = ["read", "create", "write", "delete"].map(
  (action) => `playbrain.shortLinks:${action}`
)
const shortLinksAdminMarker = "playbrain.shortLinks:publish"

export function shortLinksLevelFromPermissions(permissions: readonly string[]): ShortLinksLevel | null {
  if (permissions.includes(shortLinksAdminMarker)) return "admin"
  if (shortLinksCrudPermissions.every((permission) => permissions.includes(permission))) return "user"
  return null
}

export function dashboardPermissionsWithShortLinksLevel(
  permissions: readonly string[],
  level: ShortLinksLevel | null
): string[] {
  const withoutShortLinks = permissions.filter(
    (permission) => !permission.startsWith("playbrain.shortLinks:")
  )
  if (level === "admin") return [...withoutShortLinks, ...shortLinksCrudPermissions, shortLinksAdminMarker]
  if (level === "user") return [...withoutShortLinks, ...shortLinksCrudPermissions]
  return withoutShortLinks
}

export const dashboardPermissionFeatures: DashboardPermissionFeature[] = [
  {
    key: "playbrain-website",
    label: "Playbrain Website",
    description: "Full access to Playbrain website content, publishing, tags, history, careers, and assets.",
    resources: [
      "playbrain.website.news",
      "playbrain.website.careers",
      "playbrain.website.workProducts",
      "playbrain.website.workMarketing",
      "playbrain.website.workTags",
      "playbrain.website.companyHistory",
      "playbrain.website.assets",
    ],
  },
  {
    key: "playdata-website",
    label: "PLAYDATA Website",
    description:
      "Full access to PLAYDATA microsite content — news, terms, privacy policy — and its newsletter subscriber list.",
    resources: [
      "playbrain.website.playdataNews",
      "playbrain.website.playdataTerms",
      "playbrain.website.playdataPrivacy",
      "playbrain.website.playdataSettings",
      "playbrain.website.playdataNewsletter",
    ],
  },
  {
    key: "contract-maker",
    label: "Contract Maker",
    description: "Full access to Contract Maker.",
    resources: ["playbrain.contractMaker"],
  },
  {
    key: "temp-page",
    label: "Temp Page",
    description: "Full access to Temp Page.",
    resources: ["playbrain.tempPage"],
  },
  {
    key: "goal-setting",
    label: "Goal Setting",
    description: "Full access to Goal Setting.",
    resources: ["playbrain.goalSetting"],
  },
  {
    key: "user-management-roles",
    label: "User Management and Roles",
    description: "Full access to dashboard users, roles, role assignment, and the email template catalogue.",
    resources: ["playbrain.users", "playbrain.roles", "playbrain.emailTemplates"],
  },
  {
    key: "taiyoro-cms",
    label: "Taiyoro CMS",
    description:
      "Full access to Taiyoro CMS events, posts, meta, analytics, brackets, data collection, and scouting.",
    resources: [...taiyoroResources],
  },
  {
    key: "studio",
    label: "Studio",
    description:
      "Full access to Studio's admin tools: creating and deleting workspaces, and assigning their owners.",
    resources: ["studio.workspaces"],
  },
  // Short links has no feature preset here for the same reason it has no
  // permission-matrix group: access is one of the two Feature-access presets.
]

export function dashboardPermissionKey(
  resource: DashboardPermissionResource,
  action: DashboardPermissionAction
): DashboardPermissionKey {
  return `${resource}:${action}`
}

export function dashboardPermissionActionsForResource(
  resource: DashboardPermissionResource
): readonly DashboardPermissionAction[] {
  return dashboardPermissionActionsByResource[resource]
}

export function dashboardPermissionsForFeature(
  feature: DashboardPermissionFeature
): DashboardPermissionKey[] {
  return feature.resources.flatMap((resource) =>
    dashboardPermissionActionsForResource(resource).map((action) => dashboardPermissionKey(resource, action))
  )
}

/** Adds or removes a single `resource:action`, leaving every other one alone. */
export function dashboardPermissionsWithAction(
  selectedPermissions: readonly string[],
  resource: DashboardPermissionResource,
  action: DashboardPermissionAction,
  enabled: boolean
): string[] {
  const permission = dashboardPermissionKey(resource, action)
  const kept = selectedPermissions.filter((current) => current !== permission)
  return enabled ? [...kept, permission] : kept
}

/** Grants or clears every action a resource supports. */
export function dashboardPermissionsWithResource(
  selectedPermissions: readonly string[],
  resource: DashboardPermissionResource,
  enabled: boolean
): string[] {
  const owned = new Set<string>(
    dashboardPermissionActionsForResource(resource).map((action) => dashboardPermissionKey(resource, action))
  )
  const kept = selectedPermissions.filter((permission) => !owned.has(permission))
  return enabled ? [...kept, ...owned] : kept
}

/** Grants or clears every action across a group of resources. */
export function dashboardPermissionsWithGroup(
  selectedPermissions: readonly string[],
  resources: readonly DashboardPermissionResource[],
  enabled: boolean
): string[] {
  return resources.reduce<string[]>(
    (permissions, resource) => dashboardPermissionsWithResource(permissions, resource, enabled),
    [...selectedPermissions]
  )
}

/** How many of a resource's supported actions are currently granted. */
export function dashboardGrantedActionCount(
  selectedPermissions: Iterable<string>,
  resource: DashboardPermissionResource
): number {
  const selected = new Set(selectedPermissions)
  return dashboardPermissionActionsForResource(resource).filter((action) =>
    selected.has(dashboardPermissionKey(resource, action))
  ).length
}
